// Landing page behaviour: scroll choreography, turntable, parallax, overlays,
// and the render loop's quality/frame-skip decisions; plus the page's
// commerce and drive UI, arranged by the selected mockup layout. The image
// itself comes from cross-scene.js.
//
// The section is three layers: the stage (the scene, pinned), the drive's
// window in front of it (scrolling up beside the cross once the
// choreography's run is over), and the hud (header and buy controls, pinned
// in front of both).
//
// What differs between the standalone site and the store's theme (options,
// commerce, asset URLs) comes from the page's environment, set by the entry
// file that loads this module: js/site.js or js/theme.js (js/page-env.js).
import { createCrossScene } from "./cross-scene.js";
import { glassFromOptions, resolveGlass } from "./glass/look.js";
import { AUTO, createFallbackMonitor, pageStorage, readStored, runTrial, storeDecision } from "./glass/auto.js";
import { PHONE_QUERY, REDESIGN_NARROW_QUERY, SITE_KEYFRAMES, SITE_KEYFRAMES_PHONE, SITE_KEYFRAMES_REDESIGN_NARROW, SITE_KEYFRAMES_REDESIGN_WIDE, applyPose, poseAt } from "./pose.js";
import { PROBE_FRAMES, capWhenIdle, createFrameCap, createPacingMonitor } from "./frame-pacing.js";
import { coast, createVelocityTracker, easeFraction, frameStep, perSecond } from "./motion.js";
import { createShop } from "./shop/shop.js";
import { createBuyUI } from "./shop/buy-ui.js";
import { createDriveWindow } from "./drive/drive-window.js";
import { createLed } from "./drive/led.js";
import { createViewer } from "./drive/viewer.js";
import { OPTION, capFps, optionsSearch, replaceSearch } from "./options.js";
import { pageEnv } from "./page-env.js";
import { BAND_CLASS, DARK_CLASS, DARK_VAR, HIDDEN_CLASS, headerBand, headerDarkness, headerHidden } from "./variants/header.js";

const ENV = pageEnv();
const DRIVE = ENV.drive;

const hero = document.getElementById("hero");
const stage = document.getElementById("stage");
const hud = document.getElementById("hud");
const run = document.getElementById("run");
const canvas = document.getElementById("scene");

// The page's options (js/options.js): the URL's, or in the store the
// section's settings. A bad one is a mistake in a link we handed out (or in
// the section): say so on the page. The live ones (layout, buy, glow,
// header, fpscap) can change while the page runs: see setOptions.
let options;
try {
  options = ENV.readOptions();
} catch (err) {
  const notice = document.createElement("p");
  notice.className = "page-error";
  notice.setAttribute("role", "alert");
  notice.textContent = err.message;
  hud.append(notice);
  throw err;
}

// ?tune: the quality lab (js/tune/), a dev tool. It can also start the scene
// without antialiasing (aa=0) or with another model (glb=<path>, e.g. the
// decimated ones blender/decimate.py writes to perf/lab/): both are fixed for
// the life of the scene, so the lab reloads the page to change them. (With
// glb, the scene fetches that model itself: index.html's early fetch of the
// cross goes unused.)
const TUNE = options.tune === null ? null : { antialias: options.aa === "1", model: options.glb ?? undefined };

// ?page=redesign: the redesigned page, a mockup (js/redesign/redesign.js; see
// the end of this file). It has no layout (its bar at the bottom is the only
// buy control), and the drive's window goes in its last section. What the
// cross does after the opening is its `cross` option (js/redesign/cross.js):
// it scrolls away with the hero (as here), stays behind the page as its
// wallpaper, or comes back behind the drive at the end; wherever it is on
// the turntable it keeps turning, the drive's files open or not.
const REDESIGN = options.page === "redesign";
let redesign = null; // its handle, once its sections are on the page
// (cross=return) how far the cross has risen back into its place behind the
// drive, 0..1 (1 everywhere else): from CROSS_RISE world units below it
let crossRise = 1;
const CROSS_RISE = 1.5;
// What the page knows of its glass, published for debugging as
// window.__glass (and in ?bench's result):
//   setting   the glass option: "auto", "mtm" or "custom"
//   glass     the glass drawn now: "mtm" or "custom"
//   source    why: "explicit" (the option), "stored" (an earlier visit's
//             pick), "trial" (measured on this visit), "fallback" (MTM
//             missed frames after the reveal); null until decided
//   budgetMs  the trial's budget (AUTO.budgetMs)
//   storage   "used", or "unavailable" (none to read or write: a trial on
//             every visit)
//   stored    whether this visit's pick was stored
//   trial     the trial's verdict and frame times, if one ran
//   compileMs the custom glass's compile and switch, if this visit made one
//   decidedMs when the glass was decided (ms since navigation start)
//   fallbackMs when the fallback began, if it did
const glassAuto = {
  setting: options.glass, glass: null, source: null, budgetMs: AUTO.budgetMs,
  storage: null, stored: false, trial: null, compileMs: null, decidedMs: null, fallbackMs: null,
};
window.__glass = glassAuto;
const glassStorage = pageStorage();
glassAuto.storage = glassStorage ? "used" : "unavailable";
function startingGlass() {
  if (options.glass !== "auto") {
    Object.assign(glassAuto, { glass: options.glass, source: "explicit" });
    return glassFromOptions(options);
  }
  const stored = glassAuto.storage === "used" ? readStored(glassStorage) : null;
  if (stored === "unavailable") glassAuto.storage = "unavailable";
  else if (stored !== null) Object.assign(glassAuto, { glass: stored, source: "stored" });
  return resolveGlass({ kind: glassAuto.glass ?? "mtm" });
}

// The glass: glass=auto (the default) picks the site's glass (MTM) or the
// custom one for this device (js/glass/auto.js; see "the automatic glass"
// below), starting from the stored pick if there is one, else from MTM;
// ?glass=mtm and ?glass=custom[&look=...] (js/glass/look.js) force one.
// ?delay=<s>: the scene's downloads (models, textures) count as arrived no
// sooner than that many seconds into the page's life, as on a slow
// connection: the real loading sequence, poster rule included (index.html),
// on a fast one.
// A page that can't start the live scene at all (no WebGL: the renderer gets
// no context) shows the poster in its place, whatever the loader (with
// loader=line it is fetched only now: index.html's template#poster-image),
// and the error goes on to the console.
let crossScene;
try {
  crossScene = createCrossScene({ canvas, assetUrl: ENV.assetUrl, responses: ENV.responses, ...TUNE, glass: startingGlass(), arriveAfter: Number(options.delay) * 1000 });
} catch (err) {
  const poster = document.getElementById("poster");
  if (!poster.querySelector("img")) poster.append(document.getElementById("poster-image").content.cloneNode(true));
  stage.classList.add("is-fallback");
  document.getElementById("loader").classList.add("done");
  throw err;
}
// The lab's hold on the render loop (animate): `continuous` draws every
// frame (so frame and GPU times measure the scene, not render on demand);
// `frozen` holds the pose still (no spin, no pointer tilt) so settings can be
// compared on one image; `pose`, when set, is the pose drawn whatever the
// scroll (pose.js's [rotY, rotX, rotZ, posX, posY, camZ]: the lab's "Look
// here" poses).
const lab = { continuous: false, frozen: false, pose: null };

const baseDpr = Math.min(window.devicePixelRatio, 1.25);
let resScale = 1; // adaptive: lowered at runtime if fps sags
// the canvas fills the sticky stage (100svh), which on phones is shorter than
// the window while the URL bar is collapsed
let lastW = stage.clientWidth;
let lastH = stage.clientHeight;
// how far the layout moves the rendered cross left (px), see layouts.js
let crossShift = 0;
// render on demand: set by anything that changes the image other than the
// pose (sizes, the glow, assets arriving); see animate()
let needsRender = true;
// the transmission FBOs stay at baseDpr even after an adaptive downshift
function resize() {
  needsRender = true;
  crossScene.setSize(lastW, lastH, baseDpr * resScale, { transmissionPixelRatio: baseDpr });
  // render the frame as if the canvas were crossShift px further right, so
  // the cross lands left of centre without changing its pose
  if (crossShift) crossScene.camera.setViewOffset(lastW, lastH, crossShift, 0, lastW, lastH);
  else crossScene.camera.clearViewOffset();
}
resize();

// The backdrop (or the poster; with loader=line the loading line, which
// fills as this goes, index.html; with loader=classic the loading screen)
// stays up until the first correct frame is on screen: model, textures and
// shaders ready, then one frame drawn. Nothing is drawn before that: a draw
// would wait on the shader compiles (blocking the page) only to draw an
// image nobody sees. If the poster is showing (a slow start, see index.html),
// the first frame is the opening pose, as the poster is; once it has faded
// in over the poster, the poster goes and the pose is let go: from there it
// eases to wherever the visitor has scrolled meanwhile. Without the poster
// the first frame is already the pose for wherever the page is (a restored
// scroll position, or a visitor who scrolled while the line filled): no
// catch-up, no jump. Either way the pointer tilt is none
// until the canvas has faded in (handedOver), then eases toward the pointer
// (see animate): a pointer moved while the scene loaded doesn't jump the
// cross the moment it appears. A failed asset says so instead of loading
// forever.
const loader = document.getElementById("loader");
const poster = document.getElementById("poster");
let live = false; // the scene is ready: render
let released = false; // the handover is over: the pose follows the page
let snapPose = false; // the next frame starts at its target pose, not easing to it
let handedOver = false; // the canvas has faded in: the pointer tilt may follow the pointer
// The scene's downloads are in (the modules are, or this wouldn't run): all
// that is left before the first frame is the GPU's compile, which index.html
// covers with the backdrop rather than the poster.
crossScene.downloaded.then(() => { poster.dataset.downloads = "done"; }, () => {});
// resolves once the first live frame is drawn (in the glass decided for it:
// see "the automatic glass")
const firstFrame = crossScene.ready.then(decideGlass).then(() => {
  live = true;
  // too late for the poster (index.html): it never shows
  if (poster.dataset.state === "pending") poster.dataset.state = "skipped";
  if (poster.dataset.state === "skipped") released = snapPose = true;
  needsRender = true;
  return nextFrameDrawn();
});
// resolves once the handover is over (the live scene shown, the poster gone)
const shown = firstFrame.then(
  () => {
    loader.classList.add("done");
    stage.classList.add("is-ready");
    pacingFrom = performance.now() + PACING_WARMUP_MS;
    fallbackFrom = performance.now() + AUTO.fallback.afterMs;
    whenIdle(prefetchGlow);
    // the fades the classes just started (none with reduced motion). A
    // cancelled one (its element moved in the DOM, as ?store's chrome does)
    // jumps to its end: done too.
    const fades = [loader, canvas].flatMap((el) => el.getAnimations());
    return Promise.all(fades.map((a) => a.finished.catch((err) => {
      if (err.name !== "AbortError") throw err;
    })));
  },
  (err) => {
    loader.textContent = "Failed to load. Please reload the page.";
    loader.setAttribute("role", "alert");
    // (the loading line goes: css/hero.css)
    stage.classList.add("is-failed");
    throw err;
  },
).then(() => {
  poster.hidden = true;
  released = true;
  handedOver = true;
});

// --- scroll choreography ----------------------------------------------
// the choreography: phones get their own buy position, and the redesign its
// own, beside its text on a wide screen, above it under its breakpoint (see
// pose.js); a
// switch between them (rotation, resize) eases over like any other move
const PHONE = matchMedia(PHONE_QUERY);
const REDESIGN_NARROW = matchMedia(REDESIGN_NARROW_QUERY);
const keyframes = () => {
  if (REDESIGN) return REDESIGN_NARROW.matches ? SITE_KEYFRAMES_REDESIGN_NARROW : SITE_KEYFRAMES_REDESIGN_WIDE;
  return PHONE.matches ? SITE_KEYFRAMES_PHONE : SITE_KEYFRAMES;
};

const clamp01 = (x) => Math.min(1, Math.max(0, x));

// Narrower than this, the drive's window spans the screen (css/layouts.css),
// so the cross rises out of its way as the window comes in: by this much in
// world units, while the camera pulls back by EXIT_PULL. (Not on the
// redesign: there the drive's files are the finale, and a cross still on
// screen stays behind them.)
const NARROW = matchMedia("(max-width: 999px)");
const EXIT_RISE = 6;
const EXIT_PULL = 2;

// progress: through the choreography's run, 0 with the section's top at the
// top of the viewport, 1 at the end of the run.
// filesIn: how far the drive's window has come in, 0 with its top at the
// bottom of the viewport, 1 at its resting place (its scroll margin).
// filesShown: the window's top has passed the middle of the viewport; the
// drive counts as mounted (LED lit, turntable at rest).
let progress = 0;
let filesIn = 0;
let filesShown = false;
let restTop = 0; // the window's scroll margin, px
function measure() {
  if (driveWindow.el.isConnected) restTop = parseFloat(getComputedStyle(driveWindow.el).scrollMarginTop);
}
// progress `s` px into the hero, and back: the run's px, evenly (the
// redesign lengthens the cross's exit: its progressAt)
const runProgressAt = (s) => (redesign ? redesign.progressAt(s) : s / run.offsetHeight);
const runOffsetAt = (p) => (redesign ? redesign.offsetAt(p) : p * run.offsetHeight);
function readScroll() {
  progress = clamp01(runProgressAt(-hero.getBoundingClientRect().top));
  redesign?.setProgress(progress);
  if (redesign) placeCross(redesign.cross.update());
  // (with ?page=redesign the window is on the page once the redesign's
  // sections are: until then, not yet come in)
  const top = driveWindow.el.isConnected ? driveWindow.el.getBoundingClientRect().top : Infinity;
  const vh = window.innerHeight;
  filesIn = clamp01((vh - top) / (vh - restTop));
  filesShown = top < vh / 2;
  led.set(filesShown && options.glow === "1" && glowArrived, performance.now());
  hud.classList.toggle("is-files-shown", filesShown);
  // the header gets a band of glass once the files scroll up under it
  hud.classList.toggle("is-over-files", top < restTop - 1);
  // the store's header, in the store (variant header; see header.js)
  document.documentElement.classList.toggle(HIDDEN_CLASS,
    headerHidden(options.header, { scrollTop: document.scrollingElement.scrollTop, progress }, options.headerat));
  // and its band, over the redesign's sections after the hero
  if (redesign) {
    const headerHeight = parseFloat(getComputedStyle(document.body).getPropertyValue("--header-height")) || 0;
    // (over the scanning video: the band fading out, the text going white,
    // in step with the scroll as its edge passes under the header)
    const dark = headerDarkness({ dark: redesign.darkSections(), headerHeight });
    const root = document.documentElement;
    root.classList.toggle(DARK_CLASS, dark > 0);
    root.style.setProperty(DARK_VAR, String(Math.round(dark * 1000) / 1000));
    root.classList.toggle(BAND_CLASS, headerBand({ heroBottom: hero.getBoundingClientRect().bottom, pageBottom: redesign.pageBottom(), headerHeight }));
  }
}

const hintEl = document.getElementById("hint");
const infoEl = document.getElementById("info");

const mouse = { x: 0, y: 0 };

// turntable: active at the end of the scroll — slow auto-spin the user can
// grab and fling. All motion is per second (see motion.js), tuned per frame
// at its CALIBRATION_FPS: these are those per-frame values.
const EASE_POSE = 0.07; // the pose easing to its target
// the pointer tilt following the pointer: a time constant of ~140 ms (63%
// of the way), ~330 ms to 90%; the pose's ease is ~250 ms / ~570 ms. Quick
// enough to read as following the hand, slow enough that a pointer's first
// move (or a touch landing far from the last) glides rather than jumps.
const EASE_TILT = 0.12;
const EASE_SETTLE = 0.08; // the spin settling to the nearest full turn
const BASE_SPIN = perSecond(0.004); // rad/s, the turntable's own spin
// visitors who ask their system for reduced motion get no auto-spin (their
// own drags and flings still turn it)
const REDUCED_MOTION = matchMedia("(prefers-reduced-motion: reduce)");
const SPIN_RETAIN = 0.94; // a fling's inertia
const DRAG_RAD_PER_PX = 0.007;
let turntable = 0;
let spinY = 0;
let spinVel = 0; // rad/s
let dragging = false;
let dragPointer = 0; // the pointerId doing the dragging: other pointers don't
let lastX = 0;
const dragVelocity = createVelocityTracker();

window.addEventListener("pointerdown", (e) => {
  // only drags that start on the scene itself, never on the UI over it
  if (inView && turntable > 0.5 && e.target === canvas) {
    dragging = true;
    dragPointer = e.pointerId;
    lastX = e.clientX;
    dragVelocity.reset(spinY, e.timeStamp);
    spinVel = 0;
    e.preventDefault();
  }
});

window.addEventListener("pointermove", (e) => {
  mouse.x = (e.clientX / window.innerWidth) * 2 - 1;
  mouse.y = (e.clientY / window.innerHeight) * 2 - 1;
  if (dragging && e.pointerId === dragPointer) {
    spinY += (e.clientX - lastX) * DRAG_RAD_PER_PX;
    lastX = e.clientX;
    dragVelocity.add(spinY, e.timeStamp);
  }
});

// letting go flings the cross at the pointer's speed over its last moments
function endDrag(e) {
  if (!dragging || (e && e.pointerId !== dragPointer)) return;
  dragging = false;
  spinVel = e ? dragVelocity.velocity(e.timeStamp) : 0;
}
for (const ev of ["pointerup", "pointercancel"]) window.addEventListener(ev, endDrag);
window.addEventListener("blur", () => endDrag(null));

// Resizing reallocates the canvas buffer (visible black flash), so: debounce,
// and ignore mobile URL-bar collapse (height-only changes under 150px)
let resizeTimer = 0;
window.addEventListener("resize", () => {
  clearTimeout(resizeTimer);
  resizeTimer = setTimeout(() => {
    measure();
    readScroll();
    fitStage();
  }, 250);
});
// the canvas to the stage's size, if that has changed (but for the jiggle)
function fitStage() {
  const w = stage.clientWidth;
  const h = stage.clientHeight;
  if (w === lastW && Math.abs(h - lastH) < 150) return; // iOS toolbar jiggle
  lastW = w;
  lastH = h;
  updateCrossShift();
  resize();
}

// adaptive resolution: ONE permanent downshift when the GPU genuinely misses
// frames (see frame-pacing.js; a steady 30 fps cap is not that) — stepping
// repeatedly would flash black on each buffer reallocation. The interval after
// a frame that rendered measures the GPU; after one that didn't, the rAF
// cadence. Judging starts PACING_WARMUP_MS after the loader lifts (first
// uploads, compiles). A still page renders nothing to judge, so once the
// cadence is known the page probes: it redraws the (identical) frame for up
// to PROBE_FRAMES frames, once, so a GPU that can't keep up is found before
// the visitor first scrolls rather than during it.
const PACING_WARMUP_MS = 500;
const pacing = createPacingMonitor();
let pacingFrom = Infinity; // set when the loader lifts
let lastFrameAt = null; // rAF time of the previous frame
let lastRendered = false; // whether it rendered
let motionAt = null; // rAF time of the previous step of motion (see animate)
let downshifted = false;
let probeLeft = -1; // frames the probe still forces (-1: not started yet)
// ?bench holds the loop while it draws (see the end of this file), and so
// does the automatic glass's trial when the option switches to it live
let benchHold = false;
let trialHold = false;
// the automatic glass's fallback (js/glass/auto.js): judged from a second
// after the loader lifts, while the page draws MTM by glass=auto
const fallback = createFallbackMonitor();
let fallbackFrom = Infinity;
let fallingBack = false;
// the frame samples start over (a pause, a new cadence)
function resetPacing() {
  pacing.reset();
  fallback.reset();
  lastFrameAt = null;
}

// The frame cap (variant fpscap, see createFrameCap): the loop runs at most
// that many frames a second, skipping the rAF ticks in between; the frames
// it keeps are the ones the pacing monitor judges, so a cap reads as the
// steady cadence it is. A minute without input lowers it (capWhenIdle: the
// turntable, all that still moves then, turns on at a lower frame rate),
// and any input restores it on the next frame. A change of cap is a change
// of cadence: the monitor starts over.
const frameCap = createFrameCap(capFps(options.fpscap));
let lastInputAt = performance.now();
for (const type of ["pointerdown", "pointermove", "keydown", "wheel", "touchstart", "scroll"]) {
  // capture: scrolls inside the drive's window too
  window.addEventListener(type, () => { lastInputAt = performance.now(); }, { capture: true, passive: true });
}
function keepFrame(now) {
  const fps = capWhenIdle(capFps(options.fpscap), performance.now() - lastInputAt);
  if (fps !== frameCap.fps) {
    frameCap.fps = fps;
    resetPacing();
  }
  return frameCap.keep(now);
}
function adaptResolution(now) {
  // the quality lab sets the resolution itself (its renderScale knob), and
  // its continuous redraws would read as a GPU that can't keep up
  if (TUNE) return;
  if (lastFrameAt !== null && now > lastFrameAt) {
    const dt = now - lastFrameAt;
    // (fed on after the downshift too: the fallback reads its cadence)
    if (now >= pacingFrom && pacing.add(dt, lastRendered) && !downshifted) {
      downshifted = true;
      resScale = 0.72;
      resize();
    }
    if (glassAuto.setting === "auto" && glassAuto.glass === "mtm" && !fallingBack && now >= fallbackFrom
      && fallback.add(dt, lastRendered, pacing.cadenceMs)) fallBack();
  }
  lastFrameAt = now;
  lastRendered = false; // animate() sets it if this frame renders
  if (probeLeft < 0 && now >= pacingFrom && pacing.cadenceKnown) probeLeft = PROBE_FRAMES;
  if (downshifted) probeLeft = 0;
}

const cur = [...keyframes()[0].v];
let frameIdx = 0;

// debug: ?fps shows the frame rate in the tab title (survives even when the
// main thread is too busy for devtools script injection): the loop's frames
// per second (rAF ticks, less those the frame cap skips), and how many of
// them drew (render on demand)
const SHOW_FPS = options.fps;
let fpsFrames = 0;
let fpsDrawn = 0;
let fpsStart = performance.now();

// The render loop only runs while the stage is on screen: off-screen there is
// nothing to see, so no rAF ticks and no GPU work at all. Unknown (null)
// until the observer first reports. On the redesign the stage can also be
// on screen and out of sight, fixed behind the sections that cover it
// (js/redesign/cross.js): then too (crossShown false).
let inView = null;
let crossShown = true;
let crossFixed = false;
let rafId = 0;
new IntersectionObserver(([entry]) => {
  inView = entry.isIntersecting;
  updateLoop();
}).observe(stage);
// (the redesign) where the cross is after the opening: js/redesign/cross.js's
// crossState
function placeCross(state) {
  crossRise = state.rise;
  if (state.fixed !== crossFixed) {
    // sticky in the hero or fixed behind the page: the same size, but in a
    // store's theme the hero may be narrower than the window
    crossFixed = state.fixed;
    fitStage();
  }
  if (state.shown === crossShown) return;
  crossShown = state.shown;
  updateLoop();
}
function updateLoop() {
  const drawing = inView && crossShown;
  if (drawing && !rafId) {
    needsRender = true;
    // restart the samples so the paused interval doesn't read as slow frames
    resetPacing();
    motionAt = null;
    frameCap.reset();
    fpsStart = performance.now();
    fpsFrames = fpsDrawn = 0;
    rafId = requestAnimationFrame(animate);
    // (after: should the scroll read now put the cross out of sight, this
    // runs again and stops the loop just started)
    readScroll();
  } else if (!drawing && rafId) {
    cancelAnimationFrame(rafId);
    rafId = 0;
    endDrag(null);
  }
  // out of sight nothing will be drawn: whoever waits for a frame has it
  if (inView === false || !crossShown) for (const resolve of frameWaiters.splice(0)) resolve();
}

function animate(now) {
  rafId = requestAnimationFrame(animate);
  if (benchHold || trialHold) return;
  if (!keepFrame(now)) return;
  adaptResolution(now);
  // seconds of motion this frame (see motion.js)
  const dt = frameStep(now, motionAt);
  motionAt = now;

  if (SHOW_FPS) {
    fpsFrames++;
    const fpsEl = performance.now() - fpsStart;
    if (fpsEl >= 1000) {
      const rate = (n) => (n / fpsEl * 1000).toFixed(0);
      document.title = `NOTHING CROSS ${rate(fpsFrames)}fps ${rate(fpsDrawn)} drawn`;
      fpsFrames = fpsDrawn = 0;
      fpsStart = performance.now();
    }
  }

  // The easing below is exponential, so it never quite arrives; within
  // SETTLED of its goal it snaps there (far below a pixel), letting the pose
  // come to rest exactly so render-on-demand can stop drawing.
  // Until the handover is over, the scene stays at the top of the page: the
  // opening pose, the poster's. (Without the poster there is no handover.)
  const at = released ? progress : 0;
  const target = poseAt(keyframes(), at);
  if (NARROW.matches && released && !REDESIGN) {
    target[4] += filesIn * EXIT_RISE;
    target[5] += filesIn * EXIT_PULL;
  }
  target[4] -= (1 - crossRise) * CROSS_RISE;
  if (snapPose) {
    cur.splice(0, cur.length, ...target);
    snapPose = false;
  }
  const poseEase = easeFraction(EASE_POSE, dt);
  for (let i = 0; i < cur.length; i++) {
    const d = target[i] - cur[i];
    cur[i] = Math.abs(d) < SETTLED ? target[i] : cur[i] + d * poseEase;
  }

  turntable = Math.max(0, Math.min(1, (at - 0.92) / 0.06));
  if (!dragging && !lab.frozen) {
    // inertia + slow base spin, which stops while the drive is mounted (on
    // the redesign it keeps turning: the files are the page's finale, and
    // the cross behind them stays on the turntable)
    const fling = coast(spinVel, SPIN_RETAIN, dt);
    const spinStopped = (filesShown && !REDESIGN) || REDUCED_MOTION.matches;
    spinY += fling.distance + (spinStopped ? 0 : BASE_SPIN * turntable * dt);
    spinVel = Math.abs(fling.velocity) < SPIN_STOPPED ? 0 : fling.velocity;
  }
  // settle to the NEAREST full turn (at most half a turn of correction),
  // ramping in smoothly as the turntable fades out — no hard threshold snap
  if (!lab.frozen) {
    const settle = Math.round(spinY / (Math.PI * 2)) * (Math.PI * 2);
    const d = settle - spinY;
    spinY = Math.abs(d) < SETTLED ? settle : spinY + d * easeFraction(EASE_SETTLE * (1 - turntable), dt);
  }
  canvas.style.cursor = turntable > 0.5 ? (dragging ? "grabbing" : "grab") : "";
  // The pointer tilt eases toward the pointer, never jumps to it: none until
  // the handover is over (the live scene's first frames are the poster's, or
  // the backdrop's still opening), then it follows from there. The lab's
  // freeze holds it at none outright.
  if (lab.frozen) tilt.x = tilt.y = 0;
  else {
    const tiltEase = easeFraction(EASE_TILT, dt);
    for (const axis of ["x", "y"]) {
      const to = handedOver ? mouse[axis] : 0;
      const d = to - tilt[axis];
      tilt[axis] = Math.abs(d) < SETTLED ? to : tilt[axis] + d * tiltEase;
    }
  }

  const pose = lab.pose ?? [
    cur[0] + spinY + tilt.x * 0.1 * (1 - turntable),
    cur[1] + tilt.y * 0.06 * (1 - turntable),
    cur[2],
    cur[3],
    cur[4],
    cur[5],
  ];
  applyPose(crossScene, pose);

  hintEl.style.opacity = Math.max(0, 1 - progress * 8);
  // the product copy makes way for the drive's window
  const infoOp = Math.max(0, (progress - 0.88) / 0.12) * (1 - filesIn);
  infoEl.style.opacity = infoOp;
  infoEl.style.pointerEvents = infoOp > 0.5 ? "auto" : "none";

  led.update(performance.now());

  // While the pose is changing fast, the transmission passes (1+2 in
  // crossScene.render) refresh only every Nth frame (the quality's
  // transmissionInterval; default 1: every frame, the owner's pick over every
  // other, which roughly halves the per-frame GPU cost).
  frameIdx++;
  const moving =
    dragging ||
    Math.abs(spinVel) > SPIN_MOVING ||
    Math.abs(target[0] - cur[0]) + Math.abs(target[4] - cur[4]) > 0.02;

  // Render on demand: the GPU passes run only when the image would differ
  // from the one on screen (the canvas keeps showing its last frame). A frame
  // drawn with reused transmission passes is followed by a fresh one. (And
  // the one-off pacing probe draws, see adaptResolution, and the quality lab
  // can ask for every frame.)
  if (!live) return;
  if (!lab.continuous && probeLeft <= 0 && !needsRender && !transmissionStale && renderedPose.every((v, i) => v === pose[i])) return;
  const refreshTransmission = !moving || frameIdx % crossScene.quality.transmissionInterval === 0;
  crossScene.render({ refreshTransmission });
  lastRendered = true;
  if (probeLeft > 0) probeLeft--;
  fpsDrawn++;
  transmissionStale = !refreshTransmission;
  renderedPose = pose;
  needsRender = false;
  for (const resolve of frameWaiters.splice(0)) resolve();
}
const SETTLED = 1e-5; // radians / world units: ~0.01 px at the closest pose
// a fling's spin (rad/s): below SPIN_STOPPED it has stopped; above
// SPIN_MOVING it counts as fast motion (see `moving`)
const SPIN_STOPPED = perSecond(SETTLED / 100);
const SPIN_MOVING = perSecond(0.001);
// the pointer tilt as drawn (the pointer's -1..1 across the window, eased)
const tilt = { x: 0, y: 0 };
let renderedPose = [];
let transmissionStale = false;
// the canvas may lose its contents with the GL context, or while the tab is hidden
canvas.addEventListener("webglcontextrestored", () => { needsRender = true; });
document.addEventListener("visibilitychange", () => {
  needsRender = true;
  lastFrameAt = null; // the hidden interval is not a frame
  motionAt = null; // nor a step of motion
  frameCap.reset();
});

// resolves once the render loop has drawn a frame after this call (at once
// while the stage is off screen: then nothing is drawn, nor seen). Before the
// observer has first reported, the stage's visibility is unknown: wait for
// it, or a fast load (cached assets) would lift the loader before the scene
// had drawn anything.
const frameWaiters = [];
function nextFrameDrawn() {
  if (inView === false || !crossShown) return Promise.resolve();
  return new Promise((resolve) => frameWaiters.push(resolve));
}

// --- the automatic glass (glass=auto) ------------------------------------------
// js/glass/auto.js. Once the scene is ready, before its first frame is shown
// (the canvas still hidden behind the loading line), a page that hasn't
// decided its glass yet (none stored) holds the loop and times a few frames
// of MTM at the canvas's real size, in the first frame's pose; over budget,
// it compiles the custom glass (only then) and draws its first frame in it.
// Either way the pick is stored for the next visit. A forced glass (the
// option), or a stored pick, decides nothing here.
async function decideGlass() {
  if (glassAuto.source === null) await runGlassTrial();
  glassAuto.decidedMs = Math.round(performance.now());
}
async function runGlassTrial() {
  // a page opened in a background tab is timed once it is seen (hidden, its
  // GPU work and timers are throttled)
  if (document.hidden) {
    await new Promise((resolve) => {
      const seen = () => {
        if (document.hidden) return;
        document.removeEventListener("visibilitychange", seen);
        resolve();
      };
      document.addEventListener("visibilitychange", seen);
    });
  }
  trialHold = true;
  try {
    // the pose the loop draws next (no spin, no tilt yet at the start)
    applyPose(crossScene, lab.pose ?? cur);
    const trial = await runTrial(crossScene, () => crossScene.render());
    glassAuto.trial = trial;
    if (trial.glass === "custom") await useCustom(() => true);
    Object.assign(glassAuto, { glass: trial.glass, source: "trial" });
    if (glassAuto.storage === "used") glassAuto.stored = storeDecision(glassStorage, trial.glass, "trial", trial.medianMs);
  } finally {
    trialHold = false;
    needsRender = true;
    resetPacing();
    motionAt = null;
  }
}
// Compiles the custom glass (at its defaults: the "Match current" look),
// the page drawing on meanwhile where the browser compiles in parallel,
// then switches to it if `stillWanted()`; resolves to whether it did.
async function useCustom(stillWanted) {
  const t0 = performance.now();
  const setting = glassFromOptions({ glass: "custom", look: null });
  await crossScene.prepareGlass(setting);
  if (!stillWanted()) return false;
  crossScene.setGlass(setting);
  glassAuto.compileMs = Math.round(performance.now() - t0);
  glassAuto.glass = "custom";
  needsRender = true;
  return true;
}
// MTM missed frames after the reveal (see adaptResolution): the custom glass
// from the moment it has compiled, stored; never MTM again this visit
function fallBack() {
  fallingBack = true;
  glassAuto.fallbackMs = Math.round(performance.now());
  // (unless the lab has forced a glass meanwhile)
  useCustom(() => glassAuto.setting === "auto").then((switched) => {
    if (!switched) return;
    glassAuto.source = "fallback";
    if (glassAuto.storage === "used") glassAuto.stored = storeDecision(glassStorage, "custom", "fallback", null);
    resetPacing();
  });
}
// the option switched to auto while the page runs (the lab): this visit's
// pick if it made one, else what a load would have done
function switchToAuto() {
  glassAuto.setting = "auto";
  if (glassAuto.source === "trial" || glassAuto.source === "stored" || glassAuto.source === "fallback") {
    crossScene.setGlass(resolveGlass({ kind: glassAuto.glass }));
    needsRender = true;
    return;
  }
  const stored = glassAuto.storage === "used" ? readStored(glassStorage) : null;
  if (stored === "mtm" || stored === "custom") {
    Object.assign(glassAuto, { glass: stored, source: "stored" });
    crossScene.setGlass(resolveGlass({ kind: stored }));
    needsRender = true;
    return;
  }
  crossScene.setGlass(resolveGlass({ kind: "mtm" }));
  Object.assign(glassAuto, { glass: "mtm", source: null });
  decideGlass();
}

// --- the quality lab (?tune) ------------------------------------------------
// (Framed by the control room, lab.html mounts the same panel in its own
// sidebar instead: see the end of this file.)
const requestRender = () => { needsRender = true; };
// scrolls the page to progress p (0..1) through the choreography's run
function scrollToProgress(p) {
  scrollTo({ top: hero.offsetTop + runOffsetAt(p), behavior: "instant" });
}
const LAB_HOST = framedByLab();
if (TUNE && !LAB_HOST) {
  import("./tune/panel.js").then(({ mountTune }) => {
    mountTune({ crossScene, lab, requestRender, scrollToProgress });
  });
}

// --- the drive's LED ----------------------------------------------------
// It lights while the files are in view, like a drive mounting (variant
// flag `glow`); see readScroll.
// Each step of its fade changes the image, so it asks for a frame (render on
// demand, see animate).
const led = createLed((amount) => {
  crossScene.setGlow(amount);
  needsRender = true;
});

// The glow's bake isn't part of the first frame: it loads once the page is
// idle after the cross appears, or sooner when the visitor heads for the
// files (a Files control hovered or focused, or the drive's window within a
// viewport). The LED waits for it, so it always lights with the resin's glow
// and fades in whole, however late the bake lands.
let glowArrived = false;
function prefetchGlow() {
  if (options.glow !== "1") return;
  crossScene.loadGlow().then(() => {
    if (glowArrived) return;
    glowArrived = true;
    readScroll();
  });
}
// requestIdleCallback where there is one (not in Safari); its timeout keeps a
// page that is never idle from waiting forever
const whenIdle = (fn) =>
  "requestIdleCallback" in window ? requestIdleCallback(fn, { timeout: 2000 }) : setTimeout(fn, 1000);

// --- commerce, drive and the mockup layout --------------------------------

const shop = createShop(ENV.connector);
shop.load();

const viewer = createViewer(hero);
const driveWindow = createDriveWindow({
  baseUrl: DRIVE.photos,
  driveName: DRIVE.name,
  capacity: DRIVE.capacity,
  viewer,
});
if (!REDESIGN) document.getElementById("files").append(driveWindow.el);

const description = document.getElementById("product-description");
const readme = {
  name: "README.txt",
  // the description as plain text: one paragraph per line
  text: [...description.querySelectorAll("p")].map((p) => p.textContent.trim()).join("\n\n"),
};

const layoutContext = {
  hud,
  hero,
  driveName: DRIVE.name,
  capacity: DRIVE.capacity,
  product: {
    title: document.querySelector(".info__title").textContent,
    description: () => {
      const copy = document.createDocumentFragment();
      for (const p of description.children) copy.append(p.cloneNode(true));
      return copy;
    },
  },
  buy: (settings = {}) => createBuyUI({
    shop,
    ...ENV.buyForm,
    presentation: options.buy,
    ...settings,
  }),
  showDrive: () => driveWindow.show(),
  approachDrive: prefetchGlow,
  openReadme: (from) => viewer.showText(readme, { returnFocus: from }),
};

// The current page's layouts (js/variants/layouts.js): loaded only for it,
// so the redesign, the store's page, never fetches them (the theme doesn't
// ship them: scripts/shopify-package.mjs, NOT_IN_THEME). Until they are in
// (a module beside this one, a moment) the cross has no shift to make.
let layout = null;
const layouts = REDESIGN ? null : import("./variants/layouts.js");
const mountCurrentLayout = () => layouts.then(({ mountLayout }) => {
  layout?.destroy();
  layout = mountLayout(options.layout, layoutContext);
  updateCrossShift();
  measure();
  readScroll();
});
if (layouts) mountCurrentLayout().catch((err) => {
  console.error(err);
  const notice = document.createElement("p");
  notice.className = "page-error";
  notice.setAttribute("role", "alert");
  notice.textContent = err.message;
  hud.append(notice);
});
function updateCrossShift() {
  const shift = layout ? Math.round(layout.crossShift(lastW)) : 0;
  if (shift === crossShift) return;
  crossShift = shift;
  // the poster moves with the rendered cross (index.html)
  stage.style.setProperty("--cross-shift", `${shift}px`);
  resize();
}
updateCrossShift();

measure();
window.addEventListener("scroll", readScroll, { passive: true });
readScroll();

new IntersectionObserver(([entry], observer) => {
  if (!entry.isIntersecting) return;
  observer.disconnect();
  prefetchGlow();
}, { rootMargin: "0px 0px 100% 0px" }).observe(driveWindow.el);

// --- the featured photos (?featured) -----------------------------------------------
// Mockups of a few of the drive's photos on the page itself
// (js/featured/featured.js). The module, its stylesheet and the photos load
// only with the option on, and only once the live scene is shown: the
// cross's first frame never waits for them, and the page without them
// fetches nothing of them (tests/e2e/featured.e2e.mjs). A live option: the
// lab and the switcher change it in place (setOptions). A bad featuredlook,
// or a photo not on the drive, is said on the page.
let featured = null; // the module's handle, once asked for (a promise)
function updateFeatured() {
  if (options.featured === "off" && !featured) return;
  featured ??= import("./featured/featured.js").then(({ createFeatured }) => createFeatured({
    run,
    files: document.getElementById("files"),
    viewer,
    photos: DRIVE.photos,
  }));
  featured.then((f) => f.set(options.featured, options.featuredlook)).then(() => {
    hud.querySelector(".page-error[data-featured]")?.remove();
  }, (err) => {
    console.error(err);
    const notice = hud.querySelector(".page-error[data-featured]") ?? document.createElement("p");
    notice.className = "page-error";
    notice.dataset.featured = "";
    notice.setAttribute("role", "alert");
    notice.textContent = err.message;
    hud.append(notice);
  });
}
// Off, the page doesn't so much as wait on `shown` for it (as before the
// option). On, a scene that fails to load is still reported: this chain
// carries shown's rejection on, unhandled.
if (options.featured !== "off") shown.then(updateFeatured);

// --- the redesigned page (?page=redesign) -------------------------------------
// A mockup of the whole page redesigned (js/redesign/redesign.js): its
// module, stylesheets and media load only with the option on, once the live
// scene is shown, so the cross's first frame never waits for them; a scene
// that fails to load still gets the rest of the page (and is still reported:
// this chain carries shown's rejection on, unhandled). A redesign that fails
// to build is said on the page.
if (REDESIGN) {
  const mountRedesign = () => import("./redesign/redesign.js")
    .then(({ createRedesign }) => createRedesign({
      hero, hud, photos: DRIVE.photos, viewer, buy: layoutContext.buy,
      // the theme's section's content and stylesheets' URLs (js/theme.js);
      // the site's are content.js's and beside the page
      ...(ENV.redesign ? { sections: ENV.redesign.sections, stylesheets: ENV.redesign.stylesheets } : {}),
      // (the photo on white on top: the site's own content's only; the
      // theme's is the owner's block order)
      layoutEdit: options.layoutedit, open: options.open, white: ENV.redesign ? null : options.white, gallery: options.gallery, galleryOpen: options.galleryopen, stack: options.stack, phoneStack: options.phonestack, featured: options.featuredvideo }))
    .then((handle) => {
      handle.mountDrive(driveWindow.el);
      handle.setCross(options.cross);
      redesign = handle;
      measure();
      readScroll();
    })
    .catch((err) => {
      console.error(err);
      const notice = document.createElement("p");
      notice.className = "page-error";
      notice.setAttribute("role", "alert");
      notice.textContent = err.message;
      hud.append(notice);
    });
  shown.then(mountRedesign, (err) => {
    mountRedesign();
    throw err;
  });
}

// Changes options ({ key: value }, js/options.js) and mirrors them into the
// URL. The live ones apply at once; any other that changes reloads the page
// (the loader, say, only shows while the page loads: see it by loading
// again). Used by the ?mock switcher and the control room (lab.html).
// record: false applies live options without touching the URL (the lab's
// flicker comparison flips them several times a second); then a change
// that would need a reload throws.
function setOptions(changes, { record = true } = {}) {
  const search = optionsSearch(location.search, changes); // throws on a bad value
  const next = { ...options, ...changes };
  const changed = Object.keys(changes).filter((key) => next[key] !== options[key]);
  const reload = changed.some((key) => OPTION[key].apply !== "live");
  if (!record && reload) throw new Error(`setOptions: ${changed.filter((key) => OPTION[key].apply !== "live").join(", ")} can only change by reloading`);
  if (record) replaceSearch(window, search);
  if (reload) {
    location.reload();
    return;
  }
  const rearrange = changed.includes("layout") || changed.includes("buy");
  Object.assign(options, changes);
  if (changed.includes("glass") || changed.includes("look")) {
    if (options.glass === "auto") switchToAuto();
    else {
      crossScene.setGlass(glassFromOptions(options));
      Object.assign(glassAuto, { setting: options.glass, glass: options.glass, source: "explicit" });
      needsRender = true;
    }
  }
  if (rearrange && layouts) mountCurrentLayout();
  if (changed.includes("featured") || changed.includes("featuredlook")) shown.then(updateFeatured, () => {});
  if (changed.includes("cross")) redesign?.setCross(options.cross);
  if (changed.includes("open")) redesign?.setOpen(options.open);
  if (changed.includes("gallery")) redesign?.setGallery(options.gallery);
  if (changed.includes("galleryopen")) redesign?.setGalleryOpen(options.galleryopen);
  if (changed.includes("stack")) redesign?.setStack(options.stack);
  if (changed.includes("phonestack")) redesign?.setPhoneStack(options.phonestack);
  prefetchGlow();
  readScroll();
}

// (the switcher: only with ?mock, which the theme never sets: not shipped there)
if (options.mock) import("./variants/switcher.js").then(({ createSwitcher }) => createSwitcher({ container: hero, variants: options, onChange: setOptions }));

// --- the control room (lab.html) -----------------------------------------------
// lab.html shows this page in a frame beside its controls. Framed by it (the
// same origin, its <html data-nothingcross-lab>), and only then, the page
// publishes a handle for it: the scene and the render loop's hold (for the
// quality lab, mounted in the lab's sidebar), the options and setOptions,
// scrolling by progress, `shown` (settled once the live scene has taken
// over), and the motion the visitor drives (pointer tilt, the turntable's
// spin), which the lab's side-by-side comparison copies from one page to
// the other. On its own, or framed by anything else, the page has no such
// global.
function framedByLab() {
  if (window.parent === window) return false;
  let doc;
  try {
    doc = window.parent.document;
  } catch (err) {
    if (err.name === "SecurityError") return false; // framed by another origin
    throw err;
  }
  return doc.documentElement.hasAttribute("data-nothingcross-lab");
}
if (LAB_HOST) {
  window.__nothingcross = Object.freeze({
    crossScene,
    lab,
    requestRender,
    scrollToProgress,
    // progress through the run, unclamped (past 1: on into the files)
    progress: () => runProgressAt(document.scrollingElement.scrollTop - hero.offsetTop),
    showFiles: () => driveWindow.show(),
    // scrolls the featured photos into view, once they show (none: nothing)
    showFeatured: () => shown.then(() => featured?.then((f) => f.reveal())),
    get options() { return { ...options }; },
    setOptions,
    shown,
    get motion() { return { mouse: { ...mouse }, spinY }; },
    set motion(m) {
      Object.assign(mouse, m.mouse);
      spinY = m.spinY;
      spinVel = 0; // the leader's spin is copied each frame: no inertia of our own
    },
  });
}

// --- ?bench: measuring this device ------------------------------------------
// js/bench.js, loaded only with the option (the page without it never
// fetches it): once the live scene is shown and the adaptive resolution has
// judged the GPU, it holds the render loop (benchHold: animate draws
// nothing, so nothing but its own frames reach the GPU), draws and times
// fixed poses in each glass at the canvas's real size, and shows the
// results. When each step of the first load finished is noted here, as it
// happens, since the module may arrive after them. window.__bench is its
// state for automation (perf/browserstack.mjs): "running" from here on.
if (options.bench) {
  window.__bench = { status: "running", progress: "loading", result: null, error: null };
  const loadTimes = {};
  const at = (name) => () => { loadTimes[name] = performance.now(); };
  crossScene.downloaded.then(at("downloadedMs"), () => {});
  crossScene.ready.then(at("readyMs"), () => {});
  firstFrame.then(at("firstFrameMs"), () => {});
  shown.then(at("shownMs"), () => {});
  let glassBeforeBench = null;
  import("./bench.js").then(({ runBench }) => runBench({
    crossScene,
    shown,
    loadTimes,
    options: { ...options },
    // the choreography this page runs (phones get their own, see pose.js)
    keyframes: keyframes(),
    // the adaptive resolution's state: "off" in the quality lab (which sets
    // the resolution itself), "pending" until the probe has judged the GPU
    adaptive: () => ({
      state: TUNE ? "off" : probeLeft === 0 ? "decided" : "pending",
      downshifted,
      resScale,
      basePixelRatio: baseDpr,
    }),
    // the glass as the page decided it (window.__glass)
    glass: () => ({ ...glassAuto }),
    hold(on) {
      benchHold = on;
      if (on) {
        glassBeforeBench = crossScene.glass;
        return;
      }
      // back to the page's own glass and frame; the hold was no frame
      crossScene.setGlass(glassBeforeBench);
      // (the bench ran at the top of the page, where the LED is dark)
      crossScene.setGlow(0);
      needsRender = true;
      lastFrameAt = null;
      motionAt = null;
      frameCap.reset();
    },
  })).catch((err) => {
    // the module failed to load (bench.js reports its own errors itself)
    window.__bench = { status: "error", progress: null, result: null, error: `bench: ${err.message}` };
    const notice = document.createElement("p");
    notice.className = "page-error";
    notice.setAttribute("role", "alert");
    notice.textContent = window.__bench.error;
    hud.append(notice);
    throw err;
  });
}
